import { BaseFreeGift } from '@/stores/promotion';
import { StoreOrderLocationPickupLocationOperation } from '@/types/http';
import { BasePromotion } from '@/types/http/promotion/common';
import { ProductItem } from '@/types/product';
import { type ClassValue, clsx } from 'clsx';
import { format, parse } from 'date-fns';
import { twMerge } from 'tailwind-merge';

export const generateSlug = (str: string): string => {
  let slug = str.toLowerCase();

  slug = slug
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-') // Replace spaces with '-'
    .replace(/--+/g, '-'); // Replace multiple '-' with a single '-'

  const specialCases: { [key: string]: string } = {
    '&': 'and',
  };

  for (const char in specialCases) {
    slug = slug.replace(new RegExp(char, 'g'), specialCases[char]);
  }

  slug = slug.replace(/^-+|-+$/g, '');

  return slug;
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const isEmpty = (obj: any) =>
  [Object, Array].includes((obj || {}).constructor) && !Object.entries(obj || {}).length;

export const formatIDR = (value: string | number) =>
  `Rp${(value || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;

export const formatNumber = (value: string | number) =>
  `${(value || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

export function debounce<T extends (...args: any[]) => void>(
  callback: T,
  ms: number
): (...args: Parameters<T>) => void {
  let timer: NodeJS.Timeout | undefined;

  return (...args: Parameters<T>): void => {
    if (timer) {
      clearTimeout(timer);
    }

    timer = setTimeout(() => {
      callback(...args);
    }, ms);
  };
}

export function subdomainSuggestion(storeName: string, maxLength: number = 16): string[] {
  // Function to clean up the store name
  const cleanName = (name: string): string => {
    return name.replace(/[^a-zA-Z0-9\s]/g, '').toLowerCase();
  };

  // Function to generate permutations of an array
  const permute = (arr: string[]): string[][] => {
    if (arr.length === 0) return [[]];
    return arr.flatMap((item, i) =>
      permute([...arr.slice(0, i), ...arr.slice(i + 1)]).map((p) => [item, ...p])
    );
  };

  const cleanedName = cleanName(storeName);
  const words = cleanedName.split(/\s+/);

  // Generate permutations of the words for variety
  const permutedWords = permute(words);

  // Create subdomain suggestions

  const subdomainSuggestions = permutedWords.map((p) => {
    const joined = p.join('-');
    return joined.length > maxLength ? joined.slice(0, maxLength) : joined;
  });

  // Limit to unique suggestions and a maximum of 10
  const uniqueSuggestions = Array.from(new Set(subdomainSuggestions)).slice(0, 4);

  return uniqueSuggestions;
}

export const initialGroupFormated = (groupVariants: { label: string; values: string[] }[] = []) => {
  if (!groupVariants) {
    return [];
  }
  const formattedVariants = groupVariants.map((group) => ({
    label: group.label,
    value: group.label,
    variants: group.values.map((value) => ({
      label: value,
      value: value,
      __isNew__: true,
    })),
  }));

  return formattedVariants;
};

export const validStringIsUrl = (str: string) => {
  try {
    new URL(str);
    return true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(`failed to validate string is url`, JSON.stringify(error));
    return false;
  }
};

export const formatImageProductItems = (itemVariants: ProductItem[]) => {
  itemVariants.map((item) => {
    if (item.images && item.images.length > 0) {
      const images = item.images.map((image: { assets_url: any }) => image.assets_url);
      item.images = images;
    }
  });

  return itemVariants;
};

export const extractNumber = (value: string): number => {
  const numberOnly = value.replace(/\D/g, '');
  const parsedNumber = parseInt(numberOnly, 10);
  return isNaN(parsedNumber) ? 0 : parsedNumber;
};

export const extractPercent = (value: string): number => {
  const numberOnly = value.replace(/\D/g, '');
  const parsedNumber = parseInt(numberOnly, 10);
  return isNaN(parsedNumber) ? 0 : parsedNumber > 100 ? 100 : parsedNumber;
};

export const formatCurrency = (value: number, isDecimal: boolean = false) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'decimal',
    currency: 'IDR',
    minimumFractionDigits: isDecimal ? 2 : 0,
    maximumFractionDigits: isDecimal ? 2 : 0,
  }).format(value);
};

export function bytesToSize(bytes: number): string {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return 'n/a';
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1);
  if (i === 0) return `${bytes} ${sizes[i]}`;
  return `${(bytes / 1024 ** i).toFixed(1)} ${sizes[i]}`;
}

export function getInnerText(html: string): string {
  const safeHtml = html || '';
  if (
    typeof window !== 'undefined' &&
    typeof window.document !== 'undefined' &&
    typeof DOMParser !== 'undefined'
  ) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(safeHtml, 'text/html');
    const text = doc.body.textContent || doc.body.innerText || '';
    return text;
  }
  // Fallback for SSR: strip HTML tags and basic entities
  return removeHTMLTags(safeHtml) || '';
}

export const removeHTMLTags = (str: string) => str?.replace(/<[^>]*>|&nbsp;|&[a-z]+;|\n/g, '');

export const truncateText = (str: string, limit: number) => {
  if (str?.length > limit) {
    return str.slice(0, limit) + '...';
  }
  return str;
};

export function hexToRgb(hex: string, percent = 100) {
  hex = hex.replace(/^#/, '');

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgb(${r}, ${g}, ${b}, ${percent / 100})`;
}

export const formatEstimateDelivery = (etaFrom: string, etaTo: string) => {
  const startDate = new Date(etaFrom);
  const endDate = new Date(etaTo);
  let days = 0;

  for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
    const day = date.getDay();
    if (day !== 0 && day !== 6) {
      days++;
    }
  }

  if (days === 0) {
    return 'Today';
  } else if (days === 1) {
    return `Tomorrow`;
  } else {
    return `${days} days`;
  }
};

export const generateUniqueId = () => {
  const timestamp = Date.now();
  const randomPart = Math.random().toString(36).substring(2, 9);
  return `${timestamp}-${randomPart}`;
};

export const formatOrderStatus = (status: string) => {
  switch (status) {
    case 'ready_to_process':
      return 'Ready to process';
    case 'processing':
      return 'Processing';
    case 'shipment':
      return 'Shipment';
    case 'done':
      return 'Done';
    case 'cancel':
      return 'Cancel';
    case 'not_yet_shipped':
      return 'Not yet shipped';
    case 'shipped':
      return 'Shipped';
    case 'expired':
      return 'Expired';
    default:
      return 'Unpaid';
  }
};

export const fileExtension = (fileName: string) => {
  const ext = fileName.split('.').pop();
  if (ext) {
    return ext.toLowerCase();
  }
  return '';
};

export const formatOperatorCollection = (operator: string) => {
  switch (operator) {
    case 'is_equal_to':
      return 'is equal to';
    case 'is_not_equal_to':
      return 'is not equal to';
    case 'starts_with':
      return 'starts with';
    case 'ends_with':
      return 'ends with';
    case 'contains':
      return 'contains';
    case 'does_not_contain':
      return 'does not contain';
    case 'is_greater_than':
      return 'is greater than';
    case 'is_less_than':
      return 'is less than';
    default:
      return 'is equal to';
  }
};

export const extractYouTubeVideoId = (url: string): string | null => {
  const shortRegex =
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:shorts\/|watch\?v=)|youtu\.be\/)([^&?]{11})/;
  const longRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?]{11})/;

  const shortMatch = url?.match(shortRegex);
  const longMatch = url?.match(longRegex);

  return shortMatch ? shortMatch[1] : longMatch ? longMatch[1] : null;
};

export function dateToStringDate(date: Date, withTime = false) {
  let option: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
  if (withTime) option = { ...option, hour: 'numeric', minute: 'numeric' };
  return date.toLocaleString('id-ID', option);
}

export const formatDiscountInfo = (reward: BasePromotion['discount_reward']) => {
  if (!reward) return '';
  return reward
    .map((item) => {
      if (item.discount_type === 'amount') {
        return `${formatIDR(item.discount_amount)}`;
      } else if (item.discount_type === 'percentage') {
        return `${item.discount_amount}% (Max. ${formatIDR(item.total_discount)})`;
      }
      return '';
    })
    .join(', ');
};

export const calculateTotalDiscountPerPriority = (promo: BasePromotion[], grandTotal: number) => {
  let runningTotal = grandTotal;
  let totalDiscount = 0;

  const sortedPromos = [...promo].sort((a, b) => {
    const aPriority = a.priority === null ? Infinity : Number(a.priority);
    const bPriority = b.priority === null ? Infinity : Number(b.priority);
    return aPriority - bPriority;
  });
  const updatedPromos: (BasePromotion & { total_discount?: number })[] = [];

  for (const promo of sortedPromos) {
    if (!promo.discount_reward || promo.discount_reward.length === 0) {
      updatedPromos.push({ ...promo, total_discount: 0 });
      continue;
    }

    const discount = promo.discount_reward[0];
    let calculatedDiscount = 0;

    if (discount.discount_type === 'percentage') {
      calculatedDiscount = (discount.discount_amount / 100) * runningTotal;
    } else if (discount.discount_type === 'amount') {
      calculatedDiscount = discount.discount_amount;
    }

    if (discount.max_discount_amount > 0) {
      calculatedDiscount = Math.min(calculatedDiscount, discount.max_discount_amount);
    }

    calculatedDiscount = Math.floor(calculatedDiscount); // round down to avoid decimals

    // Ensure calculated discount does not exceed the remaining total
    if (totalDiscount + calculatedDiscount > grandTotal) {
      calculatedDiscount = grandTotal - totalDiscount;
      if (calculatedDiscount < 0) calculatedDiscount = 0;
    }

    totalDiscount += calculatedDiscount;
    runningTotal -= calculatedDiscount;

    updatedPromos.push({
      ...promo,
      total_discount: calculatedDiscount,
    });
  }

  return { updatedPromos, totalDiscount };
};

export const generatePromotionsPayload = (
  promotionsAmount: BasePromotion[],
  promotionsFreeGift?: BaseFreeGift[]
) => {
  return promotionsAmount.flatMap((promo) => {
    const hasDiscountReward = promo.discount_reward && promo.discount_reward.length > 0;
    const hasProductReward = promo.product_reward && promo.product_reward.length > 0;

    const gifts =
      promotionsFreeGift?.filter((gift) => gift.promotion_id === promo.promotion_id) ?? [];
    const items = gifts
      .flatMap((gift) => gift.items)
      .filter((item) => (item.qty ?? 0) > 0)
      .map((item) => ({
        item_id: item.item_id,
        qty: item.qty ?? 0,
      }));

    if (promo.use_voucher) {
      return {
        promotion_id: promo.promotion_id,
        voucher_code: promo.voucher_code || '',
        items,
      };
    }

    if (hasDiscountReward) {
      return {
        promotion_id: promo.promotion_id,
        voucher_code: promo.voucher_code || '',
        items,
      };
    }

    if (hasProductReward && items.length > 0) {
      return {
        promotion_id: promo.promotion_id,
        voucher_code: promo.voucher_code || '',
        items,
      };
    }

    return [];
  });
};

export function hexToRgba(hex: string | undefined | null, opacity: number = 1): string {
  if (!hex || typeof hex !== 'string') return '';

  // Hilangkan #
  hex = hex.replace(/^#/, '');

  // Shorthand support (#abc)
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('');
  }

  if (hex.length !== 6) return '';

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

type PromotionItem = { item_id: string; qty: number };
type Promotion = {
  promotion_id: string;
  voucher_code?: string;
  items?: PromotionItem[];
};

/**
 * Merges multiple promotions into a single promotion per promotion_id.
 * If multiple promotions have the same promotion_id, their voucher_code and items
 * will be merged together, ensuring no duplicate items.
 * @param promotions - Array of promotions to merge
 * @returns - Array of merged promotions
 */
export function mergePromotions(promotions: Promotion[]): Promotion[] {
  if (!Array.isArray(promotions) || promotions.length === 0) {
    return [];
  }

  const map = new Map<string, { voucher_code?: string; itemsMap: Map<string, PromotionItem> }>();

  promotions.forEach((promo) => {
    if (!promo?.promotion_id) {
      // eslint-disable-next-line no-console
      console.warn('Skipping promotion without promotion_id:', promo);
      return;
    }

    // prepare map entry for each promotion_id
    if (!map.has(promo.promotion_id)) {
      map.set(promo.promotion_id, { voucher_code: promo.voucher_code, itemsMap: new Map() });
    }
    const entry = map.get(promo.promotion_id)!;

    // merge voucher_code if it exists
    if (promo.voucher_code && promo.voucher_code !== '') {
      entry.voucher_code = promo.voucher_code;
    }

    // Merge items, ensuring no duplicates
    if (promo.items) {
      promo.items.forEach((item) => {
        if (!entry.itemsMap.has(item.item_id)) {
          entry.itemsMap.set(item.item_id, item);
        }
      });
    }
  });

  // Convert map back to array of promotions
  return Array.from(map.entries()).map(([promotion_id, { voucher_code, itemsMap }]) => ({
    promotion_id,
    ...(voucher_code ? { voucher_code } : {}),
    items: Array.from(itemsMap.values()),
  }));
}

/**
 * Parses error messages from various formats into a user-friendly string.
 * @param rawMessage - The raw error message to parse.
 * @returns A user-friendly error message.
 */
export const parseErrorMessage = (rawMessage: unknown): string => {
  type ErrorDataItem = { message?: string; promotion_id?: string };
  type ErrorObj = { message?: string; data?: ErrorDataItem[] };

  // Jika rawMessage adalah array langsung
  if (Array.isArray(rawMessage)) {
    const messages = rawMessage
      .map((item) => (typeof item === 'object' && item !== null ? item.message : undefined))
      .filter((msg): msg is string => Boolean(msg))
      .join(', ');
    return messages || 'Something went wrong';
  }

  // Jika rawMessage adalah object dengan data array
  if (
    typeof rawMessage === 'object' &&
    rawMessage !== null &&
    'data' in rawMessage &&
    Array.isArray((rawMessage as ErrorObj).data)
  ) {
    const dataArr = (rawMessage as ErrorObj).data ?? [];
    const messages = dataArr
      .map((item) => item?.message)
      .filter((msg): msg is string => Boolean(msg))
      .join(', ');
    return messages || (rawMessage as ErrorObj).message || 'Something went wrong';
  }

  if (typeof rawMessage !== 'string') {
    return 'Something went wrong';
  }

  try {
    const parsedMessage = JSON.parse(rawMessage) as ErrorObj | ErrorDataItem[];
    // Jika hasil parse adalah array
    if (Array.isArray(parsedMessage)) {
      const messages = parsedMessage
        .map((item) => (typeof item === 'object' && item !== null ? item.message : undefined))
        .filter((msg): msg is string => Boolean(msg))
        .join(', ');
      return messages || rawMessage || 'Something went wrong';
    }
    // Jika hasil parse adalah object dengan data array
    if (Array.isArray((parsedMessage as ErrorObj)?.data)) {
      const messages = ((parsedMessage as ErrorObj).data ?? [])
        .map((item) => item?.message)
        .filter((msg): msg is string => Boolean(msg))
        .join(', ');
      return (
        messages || (parsedMessage as ErrorObj)?.message || rawMessage || 'Something went wrong'
      );
    }
    return (parsedMessage as ErrorObj)?.message || rawMessage || 'Something went wrong';
  } catch {
    return rawMessage || 'Something went wrong';
  }
};

export const formatTimeRange = (timeRange: string): string => {
  try {
    if (!timeRange.includes(' - ')) return timeRange;

    const [start, end] = timeRange.split(' - ');
    const startTime = format(parse(start, 'HH:mm:ss', new Date()), 'HH:mm');
    const endTime = format(parse(end, 'HH:mm:ss', new Date()), 'HH:mm');
    return `${startTime} - ${endTime}`;
  } catch {
    return timeRange;
  }
};

export const generateTimeOptions = (stepMinutes = 30) => {
  const times = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += stepMinutes) {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute
        .toString()
        .padStart(2, '0')}`;
      times.push(timeString);
    }
  }
  return times;
};

export const formatTime12Hour = (time24: string) => {
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${hours12?.toString().padStart(2, '0')}:${minutes
    ?.toString()
    .padStart(2, '0')} ${period}`;
};

/**
 * Parses a JSON string containing latitude and longitude.
 * @param jsonString - JSON string containing latitude and longitude
 * @returns An object with lat and long properties or null if invalid
 */
export const parseCoordinates = (jsonString: string): { lat: number; long: number } | null => {
  if (!jsonString) return null;

  try {
    const parsed = JSON.parse(jsonString);

    const isValid =
      parsed &&
      typeof parsed === 'object' &&
      typeof parsed.lat === 'number' &&
      typeof parsed.long === 'number' &&
      !isNaN(parsed.lat) &&
      !isNaN(parsed.long) &&
      parsed.lat >= -90 &&
      parsed.lat <= 90 &&
      parsed.long >= -180 &&
      parsed.long <= 180 &&
      (parsed.lat !== 0 || parsed.long !== 0);

    return isValid ? { lat: parsed.lat, long: parsed.long } : null;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[parseCoordinates] Invalid JSON:', error);
    return null;
  }
};

export const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number) => {
  const R = 6371e3;
  const phi1 = (lat1 * Math.PI) / 180;
  const phi2 = (lat2 * Math.PI) / 180;
  const deltaPhi = ((lat2 - lat1) * Math.PI) / 180;
  const deltaLambda = ((lng2 - lng1) * Math.PI) / 180;

  const a =
    Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
    Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const formatPreparationTime = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  }
  const hours = minutes / 60;
  return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
};

/**
 * Parses an area string and formats it into a more readable format.
 * It removes any trailing period and number, splits by commas, and returns a formatted string.
 * @param area - The area string to parse.
 * @returns A formatted string containing the area information.
 */
export function parseArea(area: string) {
  const cleaned = area?.replace(/\.\s*\d+$/, '');
  const parts = cleaned?.split(',').map((part) => part.trim());

  if (parts?.length >= 4) {
    return `${parts[1]}, ${parts[2]}`;
  }

  return cleaned;
}

/**
 * Calculates the store's operation status for today based on the provided operations.
 * It checks if the store is open today, and returns the opening and closing times.
 * @param operations - Array of StoreOrderLocationPickupLocationOperation objects representing the store's operation hours.
 * @returns An object containing the status of the store for today, including whether it is open, and the opening and closing times.
 */
export function getTodayStoreOperationStatus(
  operations: StoreOrderLocationPickupLocationOperation[]
) {
  const now = new Date();
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const today = dayNames[now.getDay()];
  const op = operations.find((o) => o.days === today && o.is_active);

  if (!op) return null;

  const [startHour, startMin] = op.start_time.split(':').map(Number);
  const [endHour, endMin] = op.end_time.split(':').map(Number);

  const start = new Date(now);
  start.setHours(startHour, startMin, 0, 0);

  const end = new Date(now);
  end.setHours(endHour, endMin, 0, 0);

  const isOpen = now >= start && now <= end;

  return {
    isOpen,
    openTime: op.start_time.slice(0, 5), // e.g., "09:00"
    closeTime: op.end_time.slice(0, 5), // e.g., "18:00"
  };
}

export const formatDateUTC = (dateString: string) => {
  const date = new Date(dateString);
  const day = date.getUTCDate().toString().padStart(2, '0');
  const month = date.getUTCMonth(); // 0-11
  const year = date.getUTCFullYear();
  const hour = date.getUTCHours().toString().padStart(2, '0');
  const minute = date.getUTCMinutes().toString().padStart(2, '0');

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  return `${day} ${monthNames[month]} ${year} ${hour}:${minute}`;
};

export const sanitizeJsonLd = (json: object) => {
  return JSON.stringify(json).replace(/</g, '\\u003c');
};

export const breadcrumbListJsonLd = (pos: number, name: string, url: string) => ({
  '@type': 'ListItem' as const,
  position: pos,
  name,
  item: { '@id': url, name },
});

export const normalizeHost = (input: string) => {
  return input.replace(/^https?:\/\//, '').replace(/\/$/, '');
};

type Pinpoint = { lat: number; lng: number } | null;

export function normalizePinpoint(raw: unknown): Pinpoint {
  try {
    if (!raw) return null;

    let obj: any = raw;

    if (typeof raw === 'string') {
      const s = raw.trim();
      if (!s || s === '{}') return null;
      obj = JSON.parse(s.replace(/'/g, '"'));
    }

    if (typeof obj !== 'object') return null;

    // terima berbagai nama key
    const latNum = Number(obj.lat ?? obj.latitude ?? obj.latitute);
    const lngNum = Number(obj.lng ?? obj.long ?? obj.longitude);

    // invalid kalau NaN, Infinity, atau (0,0)
    if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return null;
    if (latNum === 0 && lngNum === 0) return null;

    return { lat: latNum, lng: lngNum };
  } catch {
    return null;
  }
}

export const normalizeFilterWithAll = (
  next: string[] = [],
  curr: string[] = [],
  allValue = 'all'
) => {
  if (!next.length) return [];

  const nextHasAll = next.includes(allValue);
  const currHasAll = curr.includes(allValue);

  if (nextHasAll && !currHasAll) return [allValue];

  if (nextHasAll && currHasAll && next.length > 1) {
    return next.filter((v) => v !== allValue);
  }

  if (nextHasAll && next.length === 1) return [allValue];

  return next.filter((v) => v !== allValue);
};

export const isEmptyHTML = (htmlString: string) => {
  if (!htmlString || htmlString.trim() === '') return true;

  const textContent = htmlString.replace(/<[^>]*>/g, '').trim();
  return textContent === '';
};

/**
 * Returns the fallback data if preview mode is on and the data is empty or undefined.
 * If preview mode is off, returns the original data.
 * @param data The original data.
 * @param dummy The fallback data.
 * @param preview Whether preview mode is on.
 * @returns The data to be used.
 * @template T
 */
export function previewFallback<T>(data: T[] | undefined, dummy: T[], preview: boolean): T[] {
  if (preview) {
    return data && data.length > 0 ? data : dummy;
  }
  return data ?? [];
}
